import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import format from 'date-fns/format';
import Linkify from 'linkify-react';
import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { MdClose, MdSend } from 'react-icons/md';
import type { IConversationMessage } from '../../modules/algebra/conversations';
import { LinkifyOptions } from '../../modules/algebra/linkify';
import { useEndChatOperation, useSurvivorsMessages } from '../../modules/datastores/useGetQueuePosition';
import type { ISurvivorsConversation } from '../../modules/datastores/useGetSurvivorsConversation';
import useIsTyping from '../../modules/hooks/useIsTyping';
import useOnScreen from '../../modules/hooks/useOnScreen';
import { MessagingTopics } from '../../modules/prelude/constants/topics';
import { useSocketCtx } from '../../modules/prelude/context/socket';
import { createLogger } from '../../modules/prelude/logger';
import FeedbackForm from '../feedback/FeedbackForm';
import TypingIndicator from './../../../assets/Typing-bubble-bg.gif';
import CircularProgress from '@mui/material/CircularProgress';
import useOnlineStatus from '../../modules/hooks/useOnlineStatus';
import { Typography } from '@mui/material';

interface ChatComponentProps {
  chatSession: ISurvivorsConversation;
  task_sid: string;
  messaging_service_key: string;
  captcha_token: string;
  webchat_postassessment_no_helproom: string | undefined;
  webchat_postassessment_indirect_helproom: string | undefined;
  helproom_exited: boolean;
  webchat_language: string;
}

const fileLabel = 'chat-component.tsx';
const logger = createLogger({
  fileLabel,
});

const ChatComponent: FunctionComponent<ChatComponentProps> = ({
  chatSession,
  task_sid,
  helproom_exited,
  messaging_service_key,
  captcha_token,
  webchat_postassessment_indirect_helproom = '',
  webchat_postassessment_no_helproom = '',
  webchat_language = 'en',
}) => {
  const [currentMsg, setCurrentMsg] = useState<string>('');
  const { client } = useSocketCtx();
  const { mutateAsync: mutateEndChatOperation, isLoading } = useEndChatOperation();

  const [modalOpened, setModalOpened] = useState(false);

  const messagesQuery = useSurvivorsMessages(task_sid, !chatSession.started);
  const isTyping = useIsTyping(currentMsg, 3000);
  const [userIsTyping, setUserIsTyping] = useState(isTyping);
  const isInitialMount = useRef(true);
  const isOnline = useOnlineStatus();

  /**
   * Broadcast Typing
   * @param isTyping
   */
  const broadCastTyping = useCallback(
    (typing: boolean) => {
      if (chatSession.anonymous && chatSession.id && client) {
        client.publish({
          destination: `/app${MessagingTopics.TASK_CONVERSATION_IS_TYPING}${chatSession.anonymous}`,
          headers: {
            persistent: 'true',
          },
          body: JSON.stringify({
            id: chatSession.id,
            user: 'survivor',
            isTyping: typing,
          }),
        });
      }
    },
    [chatSession.anonymous, chatSession.id, client]
  );

  useEffect(() => {
    if (!isInitialMount.current) {
      setUserIsTyping(isTyping);
    }
  }, [isTyping]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      broadCastTyping(userIsTyping);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsTyping]);

  useEffect(() => {
    if (chatSession.ended) setModalOpened(true);
  }, [chatSession.ended]);

  const getTime = (startTime: number = Date.now()) => `${format(new Date(startTime), 'MM/dd/yyyy hh:mm aaa')}`;

  /**
   * Push Message
   */
  const sendMessage = () => {
    if (currentMsg === '') return;
    const message: IConversationMessage = {
      timestamp: 0,
      sender: chatSession.id,
      message: currentMsg,
      conversation_id: chatSession.anonymous,
    };
    client?.publish({
      destination: `/app${MessagingTopics.TASK_CONVERSATION_MESSAGE}${chatSession.anonymous}`,
      headers: {
        persistent: 'true',
      },
      body: JSON.stringify(message),
    });
    setCurrentMsg('');
    setUserIsTyping(false);
  };

  const stopConversation = async () => {
    try {
      mutateEndChatOperation({
        messaging_service_key,
        task_sid,
        captchaToken: { 'g-recaptcha-response': captcha_token },
      });
    } catch (stopChatError) {
      logger.error('Error While stoping Conversation', stopChatError);
    }
  };

  const messagesEndRef = useRef<HTMLLIElement>(null);
  const isVisible = useOnScreen(messagesEndRef);
  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });

  useEffect(scrollToBottom, [chatSession.messages, messagesQuery.data?.conversations]);

  return (
    <Box className="rhs-chat flex flex-col bg-tc-6 p-6 mb-8 custom-scroll-pane" style={{ height: '500px' }}>
      {helproom_exited && chatSession.ended ? (
        <FeedbackForm dialog_open={modalOpened} form_id={webchat_postassessment_indirect_helproom} task_sid={task_sid} webchat_language={webchat_language} />
      ) : (
        <FeedbackForm dialog_open={modalOpened} form_id={webchat_postassessment_no_helproom} task_sid={task_sid} webchat_language={webchat_language} />
      )}

      {!chatSession.ended && (
        <div className="flex justify-end">
          <Button className="button-secondary" tabIndex={0} onClick={stopConversation} disabled={isLoading}>
            <MdClose className="text-lg" /> End Chat
          </Button>
        </div>
      )}
      {/* middle chat part  me - chatout, incoming- chatin*/}
      <div className="chat-container pr-5">
        <div className="chat-list-container flex  flex-col h-full">
          <div className="flex flex-col overflow-y-auto w-full">
            {!isVisible && (
              <Button className="rhs-scroll-to-new" type="button" size="small" onClick={() => messagesEndRef.current && messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })}>
                {webchat_language === 'en' ? 'Scroll To Bottom' : 'Desplazarse hacia abajo'}
              </Button>
            )}
            <div>
              <ul>
                {[...(messagesQuery.data?.conversations || []), ...chatSession.messages]
                  .sort((a: IConversationMessage, b: IConversationMessage) => a.timestamp - b.timestamp)
                  .map((message: IConversationMessage) => (
                    <div>
                      {message.message_type === 'notification' ? (
                        <div className="flex flex-no-wrap justify-center p-2">{message.message}</div>
                      ) : (
                        <div className={chatSession.id !== message.sender ? 'row chat-in' : 'row chat-out'}>
                          <div className="chat-time">
                            <div className="max-w-xl">
                              <span className="text-xs font-interbold">{chatSession.id !== message.sender ? message.display_name : 'You'}</span>&nbsp;&nbsp;
                              {getTime(message.timestamp)}
                            </div>
                          </div>
                          <div className="chat-message">
                            <div className="xs:max-w-sm md:max-w-4xl whitespace-pre-line">
                              <Linkify options={LinkifyOptions}>{message.message}</Linkify>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                <li className="h-0.5 mb-2" ref={messagesEndRef} />
              </ul>
            </div>
          </div>
        </div>
      </div>
      {chatSession.isTyping !== '' && (
        <div className="right-chat-panel">
          <div className="flex items-center gap-2">
            <Badge className="badge" variant="dot" color="primary" />
            <span className="text-xs font-interbold">{chatSession.isTyping}</span>
          </div>
          <img src={TypingIndicator} alt={chatSession.isTyping} />
        </div>
      )}
      {/* unread messages ?
        <div className="rhs-divider-container">
        <div className="label">5 minutes ago</div>
      </div>
      */}

      {/* footer part */}
      {!isOnline && <CircularProgress />}
      {!chatSession.ended && isOnline && client?.connected && (
        <div className="w-full sticky bottom-0 px-3 py-2 bg-tc-1 border-t border-b flex justify-between items-center">
          <TextField
            className="rhs-chat-input"
            placeholder={webchat_language === 'en' ? 'Type your message here...' : 'Escribe tu mensaje aquí...'}
            value={currentMsg}
            multiline
            onChange={(ev) => {
              setCurrentMsg(ev.target.value);
            }}
            onKeyPress={(ev) => {
              if (ev.key === 'Enter' && currentMsg.trim() !== '' && !ev.shiftKey) {
                sendMessage();
                ev.preventDefault();
              }
            }}
          />
          <button className="bg-pc-1 p-1 rounded-sm" type="button" onClick={sendMessage} aria-label="Send Message">
            <MdSend className="text-lg text-tc-6" />
          </button>
        </div>
      )}
      {(chatSession.ended || client?.connected === false) && <Typography variant="h6">This chat is ended. Please initiate the new chat.</Typography>}
    </Box>
  );
};
export default ChatComponent;
